/**
 * Shared drawer-bridge for public agenda views.
 *
 * Owns the section-fetch + drawer-open + prev/next wiring that both the
 * calendar view and the list view need. Each caller passes its own container
 * (for highlight/scroll-into-view scoping) and its own pulse-class name so
 * visual feedback stays scoped to that view's stylesheet.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDrawer } from "../drawer";
import { trackSectionView } from "./analytics";
const PULSE_MS = 2000;
export function restBase() {
    var _a, _b, _c;
    const cfg = (_b = (_a = window.horaAgendaList) !== null && _a !== void 0 ? _a : window.horaCalendar) !== null && _b !== void 0 ? _b : window.horaSpeakers;
    const explicit = cfg === null || cfg === void 0 ? void 0 : cfg.sectionUrl;
    if (explicit)
        return explicit.replace(/\/$/, "") + "/";
    const root = (_c = cfg === null || cfg === void 0 ? void 0 : cfg.rest) !== null && _c !== void 0 ? _c : "/wp-json/hora-advanced-agenda/v1/";
    return root.replace(/\/$/, "") + "/public/sections/";
}
// Tracks the latest in-flight section fetch so a newer click can cancel an
// older one. Without this, rapid clicks on different cards/subsections race
// and a slow earlier response can overwrite the newer drawer content.
let inFlightController = null;
let navUnbind = null;
export function fetchSection(id) {
    return __awaiter(this, void 0, void 0, function* () {
        if (inFlightController) {
            inFlightController.abort();
        }
        const controller = new AbortController();
        inFlightController = controller;
        try {
            const url = restBase() + String(id);
            const res = yield fetch(url, {
                credentials: "same-origin",
                headers: { Accept: "application/json" },
                signal: controller.signal,
            });
            if (controller.signal.aborted || !res.ok)
                return null;
            const data = (yield res.json());
            if (controller.signal.aborted)
                return null;
            if (!data || typeof data.html !== "string")
                return null;
            return data;
        }
        catch (_a) {
            return null;
        }
        finally {
            if (inFlightController === controller) {
                inFlightController = null;
            }
        }
    });
}
export function openSectionDrawer(container, sectionId, trigger, pulseClass, view) {
    return __awaiter(this, void 0, void 0, function* () {
        const data = yield fetchSection(Number(sectionId));
        if (!data)
            return;
        const drawer = getDrawer();
        drawer.open(data.html, { restoreFocusTo: trigger });
        bindNavHandler(drawer, container, pulseClass);
        pulseCard(container, sectionId, pulseClass);
        emitSectionView(data, trigger, view, "open");
        container.dispatchEvent(new CustomEvent("hora:section:open", {
            detail: { sectionId: Number(sectionId) },
            bubbles: true,
        }));
    });
}
export function bindNavHandler(drawer, container, pulseClass) {
    if (navUnbind) {
        navUnbind();
        navUnbind = null;
    }
    navUnbind = drawer.onNavigate((e) => __awaiter(this, void 0, void 0, function* () {
        if (!e.targetId)
            return;
        const data = yield fetchSection(Number(e.targetId));
        if (!data)
            return;
        drawer.setContent(data.html);
        revealCard(container, String(data.section_id));
        pulseCard(container, String(data.section_id), pulseClass);
        emitSectionView(data, null, "drawer", "nav");
    }));
    drawer.onClose(() => {
        if (navUnbind) {
            navUnbind();
            navUnbind = null;
        }
        if (inFlightController) {
            inFlightController.abort();
            inFlightController = null;
        }
    });
}
// Pulls title/type/panel-title from the trigger card when available (initial
// open from calendar/list), otherwise reads them from the freshly-injected
// drawer content wrapper (in-drawer navigation). The drawer template carries
// the same data-* attribute set on `.hora-drawer__content`.
function emitSectionView(data, trigger, view, source) {
    const source_el = trigger !== null && trigger !== void 0 ? trigger : document.querySelector(".hora-drawer__content[data-section-id]");
    const attr = (name) => { var _a; return (_a = source_el === null || source_el === void 0 ? void 0 : source_el.getAttribute(name)) !== null && _a !== void 0 ? _a : ""; };
    trackSectionView({
        sectionId: data.section_id,
        sectionTitle: attr("data-section-title"),
        sectionType: attr("data-section-type"),
        panelId: data.panel_id,
        panelTitle: attr("data-panel-title"),
        agendaId: data.agenda_id,
        view,
        source,
    });
}
export function pulseCard(container, sectionId, pulseClass) {
    const card = findCard(container, sectionId);
    if (!card)
        return;
    card.classList.remove(pulseClass);
    // Force reflow so the removed animation state is flushed before re-adding.
    void card.offsetWidth;
    card.classList.add(pulseClass);
    window.setTimeout(() => {
        card.classList.remove(pulseClass);
    }, PULSE_MS);
}
// Only used by prev/next nav. Initial click skips this — the user just
// clicked the card, so it's already in view, and triggering a smooth scroll
// while the drawer is locking body overflow desyncs the calendar grid's
// painted gridlines from the panel content.
export function revealCard(container, sectionId) {
    const card = findCard(container, sectionId);
    if (!card)
        return;
    card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
}
function findCard(container, sectionId) {
    const selector = `[data-section-id="${cssEscape(sectionId)}"]`;
    return container.querySelector(selector);
}
function cssEscape(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(value);
    }
    return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
